import React, { useMemo } from 'react';
import {
    KPICard, SplitBar, EmptyState, SectionTitle,
    getCategoryColor, DEVICE_COLORS, SOURCE_COLORS,
    formatDuration, formatNumber, formatPercent,
} from './analyticsHelpers';

// ─── Mini sparkline (inline SVG, no deps) ────────────────────────────────────
const Sparkline = ({ data, color = '#2B8EE8', height = 32 }) => {
    if (!data || data.length < 2) return null;
    const max = Math.max(...data, 1);
    const w = 80;
    const h = height;
    const pts = data
        .map((v, i) => `${(i / (data.length - 1)) * w},${h - (v / max) * h}`)
        .join(' ');
    return (
        <svg width={w} height={h} viewBox={`0 0 ${w} ${h}`} style={{ flexShrink: 0 }}>
            <polyline
                points={pts}
                fill="none"
                stroke={color}
                strokeWidth="1.5"
                strokeLinejoin="round"
                strokeLinecap="round"
                opacity="0.6"
            />
        </svg>
    );
};

// ─── Marker preview row ───────────────────────────────────────────────────────
const MarkerPreviewRow = ({ marker, rank, totalSessions }) => {
    const color = getCategoryColor(marker.category);
    return (
        <div className="marker-preview-row">
            <span className="rank-badge" style={{ color }}>{rank}</span>
            <div className="marker-preview-info">
                <span className="marker-preview-name">{marker.name}</span>
                <span className="category-pill" style={{
                    background: color + '22',
                    color,
                }}>
                    {marker.category}
                </span>
            </div>
            <div className="marker-preview-stats">
                <span className="marker-preview-clicks">{marker.clicks} clicks</span>
                <div className="mini-rate-bar-wrap">
                    <div
                        className="mini-rate-bar"
                        style={{
                            width: `${Math.min(marker.sessionClickRate, 100)}%`,
                            background: color,
                        }}
                    />
                </div>
                <span className="marker-preview-rate">{marker.sessionClickRate}%</span>
            </div>
        </div>
    );
};

// ─── Return visitor metric (computed client-side from sessions) ───────────────
// "Return visitor" = same deviceType in the same calendar day, more than once.
// We approximate using session count vs unique-day buckets per device bucket key.
const computeReturnRate = (sessions) => {
    if (!sessions || sessions.length === 0) return 0;
    // Group by (deviceType, date-string) — a rough proxy for "same device, different session"
    const buckets = {};
    sessions.forEach(s => {
        const day = new Date(s.startedAt).toDateString();
        const key = `${s.deviceType}__${day}`;
        buckets[key] = (buckets[key] || 0) + 1;
    });
    const returnSessions = Object.values(buckets).filter(v => v > 1).reduce((a, b) => a + b, 0);
    return sessions.length > 0 ? Math.round((returnSessions / sessions.length) * 100) : 0;
};

// ─── OverviewTab ─────────────────────────────────────────────────────────────
const OverviewTab = ({ analyticsData, onSeeAllMarkers }) => {
    const { kpis, markerClickRanking, categoryClicks } = analyticsData;

    const totalClicks = useMemo(
        () => markerClickRanking.reduce((a, m) => a + m.clicks, 0),
        [markerClickRanking]
    );

    const topMarker = markerClickRanking[0];

    // Bounce rate: sessions under 30s
    const bounceRate = useMemo(() => {
        if (!kpis.totalSessions) return 0;
        // We don't have raw sessions here — approximate from avgDuration heuristic
        // Real bounce rate is computed in SessionsTab where we have raw session data
        return null;
    }, [kpis]);

    // Hourly sparkline — last 12 hours relative activity
    const hourlySparkline = useMemo(() => {
        if (!kpis.hourlyActivity) return [];
        return kpis.hourlyActivity;
    }, [kpis.hourlyActivity]);

    return (
        <div className="ad-tab-content">

            {/* ── KPI Grid ── */}
            <section className="ad-section">
                <div className="kpi-grid">
                    <KPICard
                        label="Total Sessions"
                        value={formatNumber(kpis.totalSessions)}
                        sub="unique visitors"
                        accent="#2B8EE8"
                    />
                    <KPICard
                        label="Avg Session"
                        value={formatDuration(kpis.avgDurationSeconds)}
                        sub="time on map"
                        accent="#10b981"
                    />
                    <KPICard
                        label="Peak Concurrent"
                        value={kpis.peakConcurrentSessions || 0}
                        sub="at same time"
                        accent="#f59e0b"
                    />
                    <KPICard
                        label="GPS Consent"
                        value={formatPercent(kpis.gpsConsentRate)}
                        sub="location enabled"
                        accent="#06b6d4"
                    />
                    <KPICard
                        label="Total Clicks"
                        value={formatNumber(totalClicks)}
                        sub="marker interactions"
                        accent="#ec4899"
                    />
                    <KPICard
                        label="Top Marker"
                        value={topMarker?.name || '—'}
                        sub={topMarker ? `${topMarker.clicks} clicks` : 'no data yet'}
                        accent="#f97316"
                    />
                </div>
            </section>

            {/* ── Device + Source ── */}
            <section className="ad-section ad-two-col">
                <div className="ad-card">
                    <SectionTitle>Device Breakdown</SectionTitle>
                    <SplitBar data={kpis.devices} colors={DEVICE_COLORS} />
                </div>
                <div className="ad-card">
                    <SectionTitle>Entry Source</SectionTitle>
                    <SplitBar data={kpis.entrySource} colors={SOURCE_COLORS} />
                </div>
            </section>

            {/* ── Activity summary ── */}
            <section className="ad-section ad-two-col">
                <div className="ad-card">
                    <SectionTitle sub="Sessions started per hour">Hourly Activity</SectionTitle>
                    <MiniHourlyChart data={kpis.hourlyActivity} />
                </div>
                <div className="ad-card">
                    <SectionTitle sub="Clicks by category">Category Breakdown</SectionTitle>
                    <MiniCategoryChart data={categoryClicks} />
                </div>
            </section>

            {/* ── Top markers ── */}
            <section className="ad-section">
                <div className="ad-card">
                    <SectionTitle
                        action={
                            <button className="ad-see-all" onClick={onSeeAllMarkers}>
                                See all →
                            </button>
                        }
                    >
                        Top Markers
                    </SectionTitle>
                    {markerClickRanking.length === 0 ? (
                        <EmptyState message="No marker clicks recorded yet — data appears as attendees interact with the map" />
                    ) : (
                        <div className="marker-preview-list">
                            {markerClickRanking.slice(0, 5).map((m, i) => (
                                <MarkerPreviewRow
                                    key={m.markerId}
                                    marker={m}
                                    rank={i + 1}
                                    totalSessions={kpis.totalSessions}
                                />
                            ))}
                        </div>
                    )}
                </div>
            </section>

        </div>
    );
};

// ─── Mini hourly chart (compact version for overview) ────────────────────────
const MiniHourlyChart = ({ data }) => {
    if (!data) return <EmptyState message="No session data" />;
    const max = Math.max(...data, 1);
    const hours = Array.from({ length: 24 }, (_, i) => i);
    const peakHour = data.indexOf(Math.max(...data));

    return (
        <div>
            <div className="hourly-chart mini">
                {hours.map(h => (
                    <div key={h} className="hour-col">
                        <div className="hour-bar-wrap">
                            <div
                                className="hour-bar"
                                style={{
                                    height: `${(data[h] / max) * 100}%`,
                                    opacity: h === peakHour ? 1 : 0.45,
                                    background: h === peakHour ? '#f59e0b' : '#2B8EE8',
                                }}
                                title={`${h}:00 — ${data[h]} sessions`}
                            />
                        </div>
                        {h % 6 === 0 && <div className="hour-label">{h}:00</div>}
                    </div>
                ))}
            </div>
            {data[peakHour] > 0 && (
                <p className="chart-annotation">
                    Peak at {peakHour}:00 — {data[peakHour]} sessions
                </p>
            )}
        </div>
    );
};

// ─── Mini category chart ─────────────────────────────────────────────────────
const MiniCategoryChart = ({ data }) => {
    if (!data || Object.keys(data).length === 0) {
        return <EmptyState message="No clicks recorded yet" />;
    }
    const sorted = Object.entries(data).sort((a, b) => b[1] - a[1]).slice(0, 6);
    const max = Math.max(...sorted.map(([, v]) => v), 1);
    return (
        <div className="category-chart">
            {sorted.map(([cat, count]) => (
                <div key={cat} className="cat-row">
                    <span className="cat-name">{cat.replace('_', ' ')}</span>
                    <div className="cat-bar-wrap">
                        <div
                            className="cat-bar"
                            style={{
                                width: `${(count / max) * 100}%`,
                                background: getCategoryColor(cat),
                            }}
                        />
                    </div>
                    <span className="cat-count">{count}</span>
                </div>
            ))}
        </div>
    );
};

export default OverviewTab;